'use client';
import { FC, useCallback } from 'react';
import Modal from '@/layouts/Modal';
import { Button, Text } from '@/components';
import { storage } from '@/utils/storage';
import { logger } from '@/config';
import { useLocation } from '@/context/locationContext';
import { generateCartIdAction } from '@/app/actions';
import {
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  TOKEN_EXPIRY_TIME,
} from '@/constants';
import { deleteCookie, setCookie } from 'cookies-next';

interface StoreChangeModalProps {
  show: boolean;
  onClose: () => void;
  selectedStore?: any;
  pendingAddressChange?: any;
}

const StoreChangeModal: FC<StoreChangeModalProps> = ({
  show,
  onClose,
  selectedStore,
  pendingAddressChange,
}) => {
  const {
    setEmptyCart,
    setStoreChangeError,
    setPendingAddressChange,
    setDeliveryType,
    setIsClickedYes,
    isDelivery,
    setHeaderLocationTitle,
    setStoreCode,
    handleStoreChange,
  } = useLocation();
  const oldStore = storage.get('store_code');

  const updateAddressCookies = useCallback(
    async (data: any) => {
      const cookiesToSet = [
        {
          key: 'default_store_code',
          value: data?.default_store_code,
        },
        {
          key: 'store_code',
          value:
            data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE,
        },
        {
          key: 'serviceable',
          value: data?.store_code === '' ? 'false' : 'true',
        },
        { key: 'lat', value: String(data?.lat) },
        { key: 'lng', value: String(data?.lng) },
        {
          key: 'wizzy_api_key',
          value: data?.wizzy_api_key,
        },
        {
          key: 'wizzy_store_id',
          value: data?.wizzy_store_id,
        },
        {
          key: 'stock_id',
          value: data?.stock_id || DEFAULT_STOCK_ID,
        },
        {
          key: 'placeId',
          value: pendingAddressChange?.prediction?.place_id,
        },
      ];
      for (const cookie of cookiesToSet) {
        setCookie(cookie.key, cookie.value, {
          maxAge: TOKEN_EXPIRY_TIME,
        });
      }
    },
    [pendingAddressChange]
  );

  const storeChangeHandler = useCallback(async () => {
    try {
      // Clear cart data
      if (selectedStore?.code) {
        storage.set('deliveryType', 'pickup');
        setDeliveryType('pickup');
      }
      setIsClickedYes(true);
      storage.remove('cart_id');
      storage.remove('quote_id');
      storage.remove('stock_id');
      deleteCookie('stock_id');
      deleteCookie('quote_id');
      deleteCookie('cart_id');
      setEmptyCart(true);
      // Handle non-checkout case
      const homeStoreCode = storage.get('home_store_code');
      if (selectedStore?.code) {
        storage.set('address', selectedStore.name);
        setHeaderLocationTitle(selectedStore.name);
        storage.set('storeTitle', selectedStore.name);
        storage.set(
          'storeDescription',
          `${selectedStore.street[0]} ${selectedStore.street[1]}`
        );
        storage.set('deliveryType', 'pickup');
        setDeliveryType('pickup');
        await handleStoreChange({
          stock_id: selectedStore?.stock_id,
          store_code: selectedStore?.code,
          default_store_code: selectedStore?.default_store_code,
          wizzy_api_key: selectedStore?.wizzy_api_key,
          wizzy_store_id: selectedStore?.wizzy_store_id,
        });
        setStoreCode(selectedStore?.code || DEFAULT_STORE_CODE);

        if (selectedStore?.code && homeStoreCode !== selectedStore?.code) {
          setStoreChangeError(true);
          storage.set('store_change_error', 'true');
          storage.set('address', selectedStore.name);
          setHeaderLocationTitle(selectedStore.name);
          storage.set('storeTitle', selectedStore?.name);
          storage.set(
            'storeDescription',
            `${selectedStore?.street[0]} ${selectedStore?.street[1]}`
          );
        } else {
          setStoreChangeError(false);
          storage.set('store_change_error', 'false');
        }
        onClose();
        storage.set('show_location_toast', 'true');
        // Use setTimeout to ensure state updates are complete before navigation
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      if (pendingAddressChange) {
        if (pendingAddressChange.prediction.place_id) {
          await updateAddressCookies(pendingAddressChange.data);
          await handleStoreChange({
            stock_id: pendingAddressChange?.data?.stock_id,
            store_code: pendingAddressChange?.data?.store_code,
            default_store_code: pendingAddressChange?.data?.default_store_code,
            wizzy_api_key: pendingAddressChange?.data?.wizzy_api_key,
            wizzy_store_id: pendingAddressChange?.data?.wizzy_store_id,
          });
          const checkStoreValue =
            pendingAddressChange?.data?.store_code ||
            pendingAddressChange?.data?.default_store_code;
          if (checkStoreValue !== oldStore) {
            const newCartKeys = await generateCartIdAction();
            if (newCartKeys) {
              const parsedCartKeys = JSON.parse(newCartKeys);
              if (parsedCartKeys) {
                storage.set(parsedCartKeys?.key, parsedCartKeys?.value);
              }
            }
          }
        } else {
          await updateAddressCookies(pendingAddressChange.data);
          await handleStoreChange({
            stock_id: pendingAddressChange?.data?.stock_id,
            store_code: pendingAddressChange?.data?.store_code,
            default_store_code: pendingAddressChange?.data?.default_store_code,
            wizzy_api_key: pendingAddressChange?.data?.wizzy_api_key,
            wizzy_store_id: pendingAddressChange?.data?.wizzy_store_id,
          });
          const checkStoreValue =
            pendingAddressChange?.data?.store_code ||
            pendingAddressChange?.data?.default_store_code;
          if (checkStoreValue !== oldStore) {
            const newCartKeys = await generateCartIdAction();
            if (newCartKeys) {
              const parsedCartKeys = JSON.parse(newCartKeys);
              if (parsedCartKeys) {
                storage.set(parsedCartKeys?.key, parsedCartKeys?.value);
              }
            }
          }
        }
        onClose();
      }

      onClose();
    } catch (error) {
      logger.error('Error in handleStoreChange:', error);
      setEmptyCart(false);
      onClose();
    }
  }, [
    selectedStore,
    pendingAddressChange,
    onClose,
    setEmptyCart,
    setStoreChangeError,
    setPendingAddressChange,
    oldStore,
  ]);

  return (
    <Modal
      show={show}
      onClose={() => {
        setEmptyCart(false);
        setPendingAddressChange(null);
        onClose();
      }}
      closeOnOutsideClick={false}
      showCloseButton={false}
      id='store-change'
      style='transition-all duration-300 lg:rounded-lg'>
      <div className='flex flex-col items-center justify-center text-center'>
        <Text className='-mt-5 text-xl font-semibold'>
          {!isDelivery ? 'Change Your Pickup Store?' : 'Change your location?'}
        </Text>
        <Text className='my-4 text-sm text-[#38383A]'>
          {!isDelivery ? (
            <>
              <br /> Changing your pickup store will clear your cart.
              <br /> Are you sure you want to start fresh at the new store?
            </>
          ) : (
            <>
              Changing your location will update the store, which will clear
              your current cart.
              <br /> Do you want to continue?
            </>
          )}
        </Text>
        <div className='flex w-full flex-col items-center justify-center gap-4 pt-2 sm:flex-row'>
          <Button
            onClick={storeChangeHandler}
            label={
              !isDelivery ? 'Proceed with New Store' : 'Yes, change my location'
            }
            variant='secondary'
            className='w-full sm:w-auto'
          />
          <Button
            onClick={onClose}
            variant='primary'
            label={
              !isDelivery
                ? 'Keep Current Store'
                : 'No, Keep my current location'
            }
            className='w-full px-3 sm:w-auto'
          />
        </div>
      </div>
    </Modal>
  );
};

export default StoreChangeModal;
